import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import shareIcon from '../../assets/icons/share.svg';
import { toast } from 'react-toastify';
import './EventsCalendarFilters.css';
import * as myConstClass from '../../constant.js';
import enTranslations from '../../en.json';
import maTranslations from '../../ma.json';

/**
 * Events Calendar Filters component
 * Custom filter component for Events Calendar page matching the design
 */
const EventsCalendarFilters = ({ onFilterChange, initialFilters = {}, filterOptions = {}, viewMode = 'table' }) => {
    // Detect current language for translations (same pattern as EventFilters)
    const currentLanguage = localStorage.getItem('key') || 'en';
    const isMarathi = currentLanguage === 'ma';
    const lang = isMarathi ? maTranslations : enTranslations;

    // Helper to look up a key from the appropriate JSON translation file
    const t = (key) => lang[key] || enTranslations[key] || key;

    // Event types for calendar page (matching EventFilters translation logic)
    const eventTypes = [
        { id: 1, name: 'Residential Study Courses', label: t('residentialsc') },
        { id: 2, name: 'One Day Workshops', label: t('1day') },
        //Commenting out Machine Tests as per new requirements, but keeping the code for future use if needed
        // { id: 3, name: 'Machine Tests', label: t('machine_tests') },
        { id: 4, name: 'Rational Poojas', label: t('rational_poojas') },
        { id: 5, name: 'Free Events', label: t('free_events') }
    ];
    
    const [selectedEvents, setSelectedEvents] = useState(() => {
        const initial = initialFilters.events || [];
        // If 'All' is in the initial filters, return empty array (will default to 'All' in useEffect)
        if (initial.includes('All')) {
            return [];
        }
        // Map old names to new names for backward compatibility
        const mapped = initial.map(name => {
            if (name === 'Workshop' || name === '1-Day Workshops') return 'One Day Workshops';
            if (name === 'Course') return 'Residential Study Courses';
            return name;
        });
        return mapped.length > 0 ? mapped : [];
    });
    
    const [selectedLanguage, setSelectedLanguage] = useState(initialFilters.language || 'All');
    const [selectedTopics, setSelectedTopics] = useState(initialFilters.topics || ['All']);
    
    // Helper function to convert month label to value for deep-link support
    const getMonthValueFromLabel = (monthLabel, monthOptions) => {
        if (!monthLabel || monthLabel === 'All') return 'All';
        
        // If monthLabel is already a date format (YYYY-MM-DD), return it as is
        if (/^\d{4}-\d{2}-\d{2}$/.test(monthLabel)) {
            return monthLabel;
        }
        
        // Otherwise, search for the month by label in the options
        if (monthOptions && monthOptions.length > 0) {
            const monthObj = monthOptions.find(m => m.label === monthLabel);
            if (monthObj) {
                return monthObj.value;
            }
        }
        
        // If no match found, return the original value
        return monthLabel;
    };
    
    const [selectedMonth, setSelectedMonth] = useState(() => {
        const initialMonth = initialFilters.month || 'All';
        // Convert month label to value if needed (for deep-link support)
        return getMonthValueFromLabel(initialMonth, filterOptions.months || []);
    });
    const [selectedSubTopics, setSelectedSubTopics] = useState(initialFilters.subTopics || ['All']);
    const [subtopicIds, setSubtopicIds] = useState(initialFilters.subtopicIds || []);
    const [availableSubtopics, setAvailableSubtopics] = useState([]);
    const [loadingSubtopics, setLoadingSubtopics] = useState(false);
    const [selectedLocation, setSelectedLocation] = useState(initialFilters.city || 'All');
    const [selectedDuration, setSelectedDuration] = useState(initialFilters.duration || 'All');
    
    // Accordion state for mobile view
    const [openSections, setOpenSections] = useState({
        type: false,
        language: false,
        topics: false,
        month: false,
        subtopics: false,
        location: false,
         duration: false
    });
    
    const toggleSection = (sectionName) => {
        setOpenSections(prev => ({
            ...prev,
            [sectionName]: !prev[sectionName]
        }));
    };
    
    const languages = [
        { id: 'all', name: 'All', label: t('filter_all'), languageId: null },
        { id: 'english', name: 'English', label: t('filter_lang_english'), languageId: 1 },
        { id: 'marathi', name: 'Marathi', label: t('filter_lang_marathi'), languageId: 2 },
        { id: 'hindi', name: 'Hindi', label: t('filter_lang_hindi'), languageId: 3 }
    ];
    
    // Get topics from filterOptions, using Marathi label if available and Marathi is selected
    const getTopics = () => {
        if (filterOptions.topics && filterOptions.topics.length > 0) {
            const apiTopics = filterOptions.topics.map((topic, index) => {
                // Try to get the English key for translation lookup
                const topicKey = topic.nameEn || topic.name || topic.titleEn || topic.title || topic.label || '';
                // Prefer Marathi label if available and language is Marathi
                let label = t(topicKey);
                if (isMarathi && (topic.nameMr || topic.titleMr || topic.labelMr)) {
                    label = topic.nameMr || topic.titleMr || topic.labelMr;
                }
                return {
                    id: topic.id || topic.topic_id || topic.value || `topic-${index}`,
                    name: topicKey,
                    label
                };
            }).filter(topic => topic.name && topic.name !== '');
            return [
                { id: 'all', name: 'All', label: t('filter_all') },
                ...apiTopics
            ];
        }
        return [{ id: 'all', name: 'All', label: t('filter_all') }];
    };

    const topics = getTopics();

    // Keep internal state in sync when `initialFilters` prop changes (e.g., deep-link)
    useEffect(() => {
        // Events (event type) from initialFilters
        if (initialFilters.events && Array.isArray(initialFilters.events) && initialFilters.events.length > 0) {
            const categoryNameFromUrl = initialFilters.events[0];
            // Normalize mapping for common variants
            const nameMap = {
                'Residential Study Courses': 'Residential Study Courses',
                '1-Day Workshops': 'One Day Workshops',
                'One Day Workshops': 'One Day Workshops',
                'One Day Workshop': 'One Day Workshops',
                'Machine Tests': 'Machine Tests',
                'Machine Test': 'Machine Tests',
                'Rational Poojas': 'Rational Poojas',
                'Rational Pooja': 'Rational Poojas',
                'Free Events': 'Free Events',
                'Free Event': 'Free Events'
            };

            let mappedName = categoryNameFromUrl ? categoryNameFromUrl.trim() : 'Residential Study Courses';
            if (nameMap[mappedName]) {
                mappedName = nameMap[mappedName];
            } else {
                const lower = mappedName.toLowerCase();
                for (const [key, value] of Object.entries(nameMap)) {
                    if (key.toLowerCase() === lower) {
                        mappedName = value;
                        break;
                    }
                }
            }

            setSelectedEvents(prev => {
                const current = prev && prev[0] ? prev[0] : '';
                if (current !== mappedName) return [mappedName];
                return prev;
            });
        } else if (!initialFilters.events || (Array.isArray(initialFilters.events) && initialFilters.events.length === 0)) {
            setSelectedEvents(prev => {
                if (!prev || prev.length === 0) return ['Residential Study Courses'];
                return prev;
            });
        }

        // Topics
        if (initialFilters.topics !== undefined && Array.isArray(initialFilters.topics)) {
            const normalizeTopicName = (topicName) => {
                if (!topicName || topicName === 'All') return topicName;
                if (filterOptions.topics && filterOptions.topics.length > 0) {
                    const normalizedInput = topicName.toString().trim().toLowerCase();

                    const extractVariants = (t) => [
                        t.name,
                        t.nameEn,
                        t.nameMr,
                        t.title,
                        t.titleEn,
                        t.titleMr,
                        t.topic_title,
                        t.topic_title_en,
                        t.topic_title_mr,
                        t.label
                    ].filter(Boolean).map(v => v.toString().trim().toLowerCase());

                    const matchingTopic = filterOptions.topics.find(t => {
                        const variants = extractVariants(t);
                        // Exact match first
                        if (variants.some(v => v === normalizedInput)) return true;
                        // Substring/partial match (handles cases like "Nurturing Kids: Birth up to 7 years" vs "Birth up to 7 years")
                        if (variants.some(v => v.includes(normalizedInput) || normalizedInput.includes(v))) return true;
                        // Try matching after removing text before ':' or inside parentheses
                        const normalizedInputAfterColon = normalizedInput.includes(':') ? normalizedInput.split(':').pop().trim() : normalizedInput;
                        if (variants.some(v => v.includes(normalizedInputAfterColon) || normalizedInputAfterColon.includes(v))) return true;
                        const normalizedInputParens = normalizedInput.replace(/\(.*\)/, '').trim();
                        if (variants.some(v => v.includes(normalizedInputParens) || normalizedInputParens.includes(v))) return true;
                        return false;
                    });

                    if (matchingTopic) {
                        return matchingTopic.nameEn || matchingTopic.name || matchingTopic.titleEn || matchingTopic.title || topicName;
                    }
                }
                return topicName;
            };

            const normalizedTopicsRaw = initialFilters.topics.length > 0 ? initialFilters.topics.map(normalizeTopicName) : ['All'];
            // Deduplicate while preserving order
            const normalizedTopics = Array.from(new Set(normalizedTopicsRaw.map(t => (t || '').toString()))).filter(t => t !== '');

            setSelectedTopics(prev => {
                const prevStr = prev.join(',');
                const newStr = normalizedTopics.join(',');
                if (prevStr !== newStr) return normalizedTopics;
                return prev;
            });
        }

        if (initialFilters.language) {
            setSelectedLanguage(initialFilters.language);
        }

        if (initialFilters.subTopics) {
            setSelectedSubTopics(initialFilters.subTopics);
        }

        if (initialFilters.duration !== undefined) {
            setSelectedDuration(initialFilters.duration);
        }
        
        // Handle month filter - convert label to value if needed (for deep-link support)
        if (initialFilters.month !== undefined) {
            const monthValue = getMonthValueFromLabel(initialFilters.month, filterOptions.months || []);
            setSelectedMonth(prev => {
                if (prev !== monthValue) return monthValue;
                return prev;
            });
        }
    }, [
        initialFilters.events ? initialFilters.events.join(',') : '',
        initialFilters.topics ? initialFilters.topics.join(',') : '',
        initialFilters.language,
        initialFilters.subTopics ? initialFilters.subTopics.join(',') : '',
        initialFilters.month,
        initialFilters.duration,
        // Re-run when filterOptions.topics arrive/change so normalization can match against API data
        filterOptions.topics ? filterOptions.topics.map(t => (t.id || t.topic_id || t.value || '').toString()).join(',') : '',
        // Re-run when filterOptions.months arrive/change so month label can be converted to value
        filterOptions.months ? filterOptions.months.map(m => m.value || '').join(',') : ''
    ]);
    
    // Get months from filterOptions
    const getMonths = () => {
        if (filterOptions.months && filterOptions.months.length > 0) {
            return [
                { value: 'All', label: t('all_months') },
                ...filterOptions.months
            ];
        }
        return [{ value: 'All', label: t('all_months') }];
    };

    const months = getMonths();
    
    // Get locations/cities from filterOptions
    const getLocations = () => {
        
        if (filterOptions.cities && filterOptions.cities.length > 0) {
            // Transform API cities to component format
            // Try multiple possible field name variations from the API
            const apiCities = filterOptions.cities.map((city, index) => {
                // Try various field name combinations
                const cityId = city.city_id || city.id || city.cityId || null;
                const cityName = city.city_name || city.name || city.nameEn || city.nameMr || 
                                city.label || city.cityName || '';
                
                
                return {
                    id: cityId || `city-${index}`,
                    value: cityId || '',
                    label: cityName
                };
            }).filter(city => city.label && city.label !== '' && city.value); // Filter out empty labels and values
            
            
            return [
                { id: 'all', value: 'All', label: 'All' },
                ...apiCities
            ];
        }
        return [{ id: 'all', value: 'All', label: 'All' }];
    };
    
    const locations = getLocations();
    
    // Fetch subtopics when topics are selected (supports multiple topics)
    useEffect(() => {
        const fetchSubtopics = async () => {
            // If no topics selected or "All" is selected, clear subtopics
            if (!selectedTopics || selectedTopics.length === 0 || selectedTopics.includes('All')) {
                setAvailableSubtopics([]);
                setSelectedSubTopics(['All']);
                return;
            }

            // Get topic IDs from selected topic names
            const getTopicIds = () => {
                if (filterOptions.topics && filterOptions.topics.length > 0) {
                    return selectedTopics
                        .map(topicName => {
                            const normalizedInput = topicName.trim().toLowerCase();
                            const topic = filterOptions.topics.find(t => {
                                const topicNameVariants = [
                                    t.name,
                                    t.nameEn,
                                    t.nameMr,
                                    t.title,
                                    t.titleEn,
                                    t.titleMr,
                                    t.topic_title,
                                    t.topic_title_en,
                                    t.topic_title_mr,
                                    t.label
                                ];
                                return topicNameVariants.some(variant =>
                                    variant && variant.toString().trim().toLowerCase() === normalizedInput
                                );
                            });
                            return topic ? (topic.id || topic.topic_id) : null;
                        })
                        .filter(id => id !== null && id !== undefined);
                }
                return [];
            };

            const topicIds = getTopicIds();

            if (topicIds.length === 0) {
                setAvailableSubtopics([]);
                return;
            }

            try {
                setLoadingSubtopics(true);
                const response = await fetch(`${myConstClass.NODE_API_BASE}/topics/subtopics?language=${currentLanguage}`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                        topic_ids: topicIds
                    }),
                });

                const data = await response.json();

                if (data.status === 'true' && data.data) {
                    const transformedSubtopics = data.data.map((subtopic) => ({
                        id: subtopic.value || subtopic.id,
                        name: subtopic.label || subtopic.name || '',
                        label: subtopic.label || subtopic.name || ''
                    }));

                    setAvailableSubtopics(transformedSubtopics);
                    // Reset selected subtopics to All or preserve valid initial selection
                    const currentSelected = selectedSubTopics.filter(st => st !== 'All');
                    if (currentSelected.length > 0) {
                        const validSelected = currentSelected.filter(selectedName =>
                            transformedSubtopics.some(st => (st.name && st.name === selectedName) || (st.label && st.label === selectedName))
                        );
                        if (validSelected.length > 0) {
                            setSelectedSubTopics(validSelected);
                        } else {
                            setSelectedSubTopics(['All']);
                        }
                    } else {
                        setSelectedSubTopics(['All']);
                    }
                } else {
                    setAvailableSubtopics([]);
                }
            } catch (error) {
                console.error('Error fetching subtopics:', error);
                setAvailableSubtopics([]);
            } finally {
                setLoadingSubtopics(false);
            }
        };

        fetchSubtopics();
    }, [selectedTopics, filterOptions.topics, currentLanguage]);
    
    // Auto-select subtopics from URL (deep link support)
    useEffect(() => {
        // Only proceed if:
        // 1. We have subtopics loaded from API
        // 2. We have subtopic IDs from URL (initialFilters)
        if (availableSubtopics.length > 0 && 
            initialFilters.subtopicIds && 
            initialFilters.subtopicIds.length > 0) {
            
            // Match subtopic IDs from URL with loaded subtopics
            const matchedSubtopics = [];
            const matchedIds = [];
            
            initialFilters.subtopicIds.forEach(urlSubtopicId => {
                const found = availableSubtopics.find(st => {
                    const stId = st.id || st.value;
                    return stId && parseInt(stId, 10) === parseInt(urlSubtopicId, 10);
                });
                
                if (found) {
                    const subtopicName = found.name || found.label;
                    matchedSubtopics.push(subtopicName);
                    matchedIds.push(found.id || found.value);
                }
            });
            
            // Update selectedSubTopics if we found matches
            if (matchedSubtopics.length > 0) {
                setSelectedSubTopics(matchedSubtopics);
                setSubtopicIds(matchedIds);
            }
        }
    }, [availableSubtopics]); // Only trigger when subtopics are loaded
    
    // Use dynamic subtopics only when topic(s) selected (not 'All'). Return empty when 'All' is selected.
    const getSubTopics = () => {
        if (!selectedTopics || selectedTopics.length === 0 || selectedTopics.includes('All')) {
            return [];
        }

        if (availableSubtopics && availableSubtopics.length > 0) {
            return [
                { id: 'all', name: 'All', label: 'All' },
                ...availableSubtopics
            ];
        }

        return [];
    };
    
    const subTopics = getSubTopics();
    
    // Handle event type checkbox change - enforce single selection (like EventFilters)
    const handleEventTypeChange = (eventName) => {
        // Only allow one event to be selected at a time
        // If the clicked event is already selected, keep it selected (don't deselect)
        const updatedEvents = [eventName];
        setSelectedEvents(updatedEvents);
        
        // Notify parent immediately
        onFilterChange({
            events: updatedEvents,
            language: selectedLanguage,
            topics: selectedTopics,
            month: selectedMonth,
            subTopics: selectedSubTopics,
            subtopicIds,
            city: selectedLocation,
            duration: selectedDuration
        });
    };
    
    // Handle language change
    const handleLanguageChange = (language) => {
        setSelectedLanguage(language);
        
        // Notify parent immediately
        onFilterChange({
            events: selectedEvents,
            language: language,
            topics: selectedTopics,
            month: selectedMonth,
            subTopics: selectedSubTopics,
            subtopicIds,
            city: selectedLocation,
            duration: selectedDuration
        });
    };

    // Handle sharing filters (deep-link)
    const handleShare = async (e) => {
        if (e && e.stopPropagation) e.stopPropagation();

        try {
            const origin = window && window.location && window.location.origin ? window.location.origin : '';
            // Build query string manually to avoid encoding commas
            const queryParts = [];

            // Event type - resolve to ID
            if (selectedEvents && selectedEvents.length > 0 && selectedEvents[0] !== 'All') {
                const eventTypeName = selectedEvents[0];
                const eventType = eventTypes.find(et => et.name === eventTypeName || et.label === eventTypeName);
                if (eventType && eventType.id) {
                    queryParts.push(`event_type_id=${encodeURIComponent(eventType.id)}`);
                }
            }

            // Topics - resolve to IDs
            if (selectedTopics && selectedTopics.length > 0 && !selectedTopics.includes('All')) {
                const getTopicIds = () => {
                    return selectedTopics
                        .map(topicName => {
                            if (!filterOptions.topics || filterOptions.topics.length === 0) return null;
                            const normalizedTopicName = topicName.trim().toLowerCase();
                            const topic = filterOptions.topics.find(t => {
                                const possibleNames = [
                                    t.name,
                                    t.nameEn,
                                    t.nameMr,
                                    t.label,
                                    t.title,
                                    t.titleEn,
                                    t.titleMr,
                                    t.topic_title,
                                    t.topic_title_en,
                                    t.topic_title_mr
                                ];
                                return possibleNames.some(fieldValue => 
                                    fieldValue && fieldValue.toString().trim().toLowerCase() === normalizedTopicName
                                );
                            });
                            return topic ? (topic.id || topic.topic_id) : null;
                        })
                        .filter(id => id !== null && id !== undefined);
                };
                
                const topicIds = getTopicIds();
                if (topicIds.length > 0) {
                    queryParts.push(`topic_id=${topicIds.join(',')}`);
                }
            }

            // Language - use language_id instead of language name for deep link
            if (selectedLanguage && selectedLanguage !== 'All') {
                // Find language object by name
                const langObj = languages.find(l => l.name === selectedLanguage);
                if (langObj && langObj.languageId) {
                    queryParts.push(`language_id=${langObj.languageId}`);
                }
            }

            // Subtopics - use subtopic IDs
            if (subtopicIds && subtopicIds.length > 0) {
                queryParts.push(`subtopic_id=${subtopicIds.join(',')}`);
            }

            // Month - use month value (not label) for URL parameter
            if (selectedMonth && selectedMonth !== 'All') {
                queryParts.push(`month=${encodeURIComponent(selectedMonth)}`);
            }

            // City / Location - keep this as per requirements
            if (selectedLocation && selectedLocation !== 'All' && selectedLocation !== 'Select') {
                queryParts.push(`location=${encodeURIComponent(selectedLocation)}`);
            }

            // Duration
            if (selectedDuration && selectedDuration !== 'All') {
                queryParts.push(`duration=${encodeURIComponent(selectedDuration)}`);
            }

            // View mode
            queryParts.push(`view=${viewMode || 'table'}`);

            // Use fixed base path for calendar page (matches listing page deep link)
            const baseUrl = `${origin}/event-calendar`;
            const shareUrl = queryParts.length > 0 ? `${baseUrl}?${queryParts.join('&')}` : baseUrl;

            // Try native share API
            if (typeof navigator !== 'undefined' && navigator.share) {
                await navigator.share({
                    title: 'Manashakti Events',
                    text: 'Check out these events',
                    url: shareUrl
                });
                return true;
            }

            // Fallback: copy to clipboard
            if (typeof navigator !== 'undefined' && navigator.clipboard && navigator.clipboard.writeText) {
                await navigator.clipboard.writeText(shareUrl);
                toast.success('Link copied to clipboard!');
                return true;
            }

            // Final fallback
            window.prompt('Copy this link:', shareUrl);
            return true;
        } catch (err) {
            console.error('Share failed', err);
            if (err.name !== 'AbortError') {
                toast.error('Failed to share link');
            }
            return false;
        }
    };
    
    // Handle topic change (multi-select like EventFilters)
    const handleTopicChange = (topicName) => {
        setSelectedTopics(prevTopics => {
            let updatedTopics;

            if (topicName === 'All') {
                updatedTopics = ['All'];
            } else {
                if (prevTopics.includes('All')) {
                    updatedTopics = [topicName];
                } else {
                    if (prevTopics.includes(topicName)) {
                        updatedTopics = prevTopics.filter(t => t !== topicName);
                        if (updatedTopics.length === 0) {
                            updatedTopics = ['All'];
                        }
                    } else {
                        updatedTopics = [...prevTopics, topicName];
                    }
                }
            }

            // Clear subtopics when topics change
            setSelectedSubTopics(['All']);
            
            // Notify parent immediately
            onFilterChange({
                events: selectedEvents,
                language: selectedLanguage,
                topics: updatedTopics,
                month: selectedMonth,
                subTopics: ['All'],
                subtopicIds: [],
                city: selectedLocation,
                duration: selectedDuration
            });

            return updatedTopics;
        });
    };
    
    // Handle month change
    const handleMonthChange = (month) => {
        setSelectedMonth(month);
        
        // Notify parent immediately
        onFilterChange({
            events: selectedEvents,
            language: selectedLanguage,
            topics: selectedTopics,
            month: month,
            subTopics: selectedSubTopics,
            subtopicIds,
            city: selectedLocation,
            duration: selectedDuration
        });
    };

    // Handle duration change
    const handleDurationChange = (duration) => {
        setSelectedDuration(duration);
        
        // Notify parent immediately
        onFilterChange({
            events: selectedEvents,
            language: selectedLanguage,
            topics: selectedTopics,
            month: selectedMonth,
            subTopics: selectedSubTopics,
            subtopicIds,
            city: selectedLocation,
            duration: duration
        });
    };
    
    // Handle subtopic change
    const handleSubTopicChange = (subtopicName) => {
        setSelectedSubTopics(prev => {
            let updatedSubTopics;
            if (subtopicName === 'All') {
                updatedSubTopics = ['All'];
            } else {
                const isSelected = prev.includes(subtopicName);
                if (isSelected) {
                    const filtered = prev.filter(name => name !== subtopicName);
                    updatedSubTopics = filtered.length === 0 ? ['All'] : filtered;
                } else {
                    const filtered = prev.filter(name => name !== 'All');
                    updatedSubTopics = [...filtered, subtopicName];
                }
            }
            
            // Get subtopic IDs for the selected subtopics
            const getSubtopicIds = () => {
                if (updatedSubTopics.includes('All') || updatedSubTopics.length === 0) {
                    return [];
                }
                return updatedSubTopics
                    .map(subtopicName => {
                        const subtopic = availableSubtopics.find(st => 
                            st.name === subtopicName || st.label === subtopicName
                        );
                        return subtopic ? (subtopic.id || subtopic.value) : null;
                    })
                    .filter(id => id !== null && id !== undefined);
            };
            
            const updatedSubtopicIds = getSubtopicIds();
            setSubtopicIds(updatedSubtopicIds);
            
            // Notify parent immediately
            onFilterChange({
                events: selectedEvents,
                language: selectedLanguage,
                topics: selectedTopics,
                month: selectedMonth,
                subTopics: updatedSubTopics,
                subtopicIds: updatedSubtopicIds,
                city: selectedLocation,
                duration: selectedDuration
            });
            
            return updatedSubTopics;
        });
    };
    
    // Handle location change
    const handleLocationChange = (location) => {
        setSelectedLocation(location);
        
        // Notify parent immediately
        onFilterChange({
            events: selectedEvents,
            language: selectedLanguage,
            topics: selectedTopics,
            month: selectedMonth,
            subTopics: selectedSubTopics,
            subtopicIds,
            city: location,
            duration: selectedDuration
        });
    };
    
    // Note: Filter change notifications are now handled directly in each handler function
    // to avoid infinite render loops caused by onFilterChange dependency changes
    
    return (
        <div className="events-calendar-filters">
            {/* FILTERS Title */}
            <div className="event-filters__header">
                <h3 className="event-filters__title">{t('filter_title')}</h3>
                <button
                    type="button"
                    onClick={handleShare}
                    aria-label={t('filter_share_aria')}
                    title={t('filter_share_title')}
                    className="event-filters__share-btn"
                >
                    <img src={shareIcon} alt="Share" className="event-filters__share-icon" />
                </button>
            </div>
            
            {/* Type Section */}
            <div className="events-calendar-filters__section">
                {/* Mobile Accordion Header */}
                <button 
                    className="events-calendar-filters__accordion-header d-lg-none"
                    onClick={() => toggleSection('type')}
                    aria-expanded={openSections.type}
                >
                    <h4 className="events-calendar-filters__section-title">{t('filter_type_label')}</h4>
                    <i className={`fas fa-chevron-${openSections.type ? 'up' : 'down'} events-calendar-filters__chevron`}></i>
                </button>
                
                {/* Desktop Title */}
                <h4 className="events-calendar-filters__section-title d-none d-lg-block">{t('filter_type_label')}</h4>
                
                {/* Options */}
                <div className={`events-calendar-filters__options ${!openSections.type ? 'events-calendar-filters__options--accordion' : ''} d-lg-block`}>
                    {eventTypes.map((eventType) => (
                        <label key={eventType.id} className="events-calendar-filters__checkbox-label">
                            <input
                                type="checkbox"
                                checked={selectedEvents.includes(eventType.name)}
                                onChange={() => handleEventTypeChange(eventType.name)}
                                className="events-calendar-filters__checkbox"
                            />
                            <span className="events-calendar-filters__checkbox-text">{eventType.label}</span>
                        </label>
                    ))}
                </div>
            </div>
            
            {/* Language Section */}
            <div className="events-calendar-filters__section" data-open={openSections.language}>
                {/* Mobile Accordion Header */}
                <button 
                    className="events-calendar-filters__accordion-header d-lg-none"
                    onClick={() => toggleSection('language')}
                    aria-expanded={openSections.language}
                >
                    <h4 className="events-calendar-filters__section-title">{t('filter_language_section')}</h4>
                    <i className={`fas fa-chevron-${openSections.language ? 'up' : 'down'} events-calendar-filters__chevron`}></i>
                </button>
                
                {/* Desktop Title */}
                <h4 className="events-calendar-filters__section-title d-none d-lg-block">{t('filter_language_section')}</h4>
                
                {/* Language Tabs */}
                <div className={`events-calendar-filters__options ${!openSections.language ? 'events-calendar-filters__options--accordion' : ''} d-lg-block`}>
                    <div className="events-calendar-filters__language-tabs">
                        {languages.map((lang) => (
                            <button
                                key={lang.id}
                                className={`events-calendar-filters__language-tab ${selectedLanguage === lang.name ? 'active' : ''}`}
                                onClick={() => handleLanguageChange(lang.name)}
                            >
                                {lang.label}
                            </button>
                        ))}
                    </div>
                </div>
            </div>

            {/* Event Duration Section */}
            <div
                className="events-calendar-filters__section"
                data-open={openSections.duration}
            >
                {/* Mobile Accordion Header */}
                <button
                    className="events-calendar-filters__accordion-header d-lg-none"
                    onClick={() => toggleSection('duration')}
                    aria-expanded={openSections.duration}
                >
                    <h4 className="events-calendar-filters__section-title">{t('duration_all')}</h4>
                    <i
                        className={`fas fa-chevron-${
                            openSections.duration ? 'up' : 'down'
                        } events-calendar-filters__chevron`}
                    ></i>
                </button>

                {/* Desktop Title */}
                <h4 className="events-calendar-filters__section-title d-none d-lg-block">
                    {t('duration_all')}
                </h4>

                {/* Options */}
                <div className="events-calendar-filters__options events-calendar-filters__options--accordion d-lg-block">
                    <select
                        className="events-calendar-filters__select"
                        value={selectedDuration}
                        onChange={(e) => handleDurationChange(e.target.value)}
                    >
                        <option value="All">{t('duration_All')}</option>
                        <option value="1 day">{t('duration_1day')}</option>
                        <option value="2 days">{t('duration_2days')}</option>
                        <option value="3 days">{t('duration_3days')}</option>
                        <option value="4+ days">{t('duration_4plus')}</option>
                    </select>
                </div>
            </div>

            
            {/* Month Section */}
            <div className="events-calendar-filters__section" data-open={openSections.month}>
                {/* Mobile Accordion Header */}
                <button 
                    className="events-calendar-filters__accordion-header d-lg-none"
                    onClick={() => toggleSection('month')}
                    aria-expanded={openSections.month}
                >
                    <h4 className="events-calendar-filters__section-title">{t('filter_month_section')}</h4>
                    <i className={`fas fa-chevron-${openSections.month ? 'up' : 'down'} events-calendar-filters__chevron`}></i>
                </button>
                
                {/* Desktop Title */}
                <h4 className="events-calendar-filters__section-title d-none d-lg-block">{t('filter_month_section')}</h4>
                
                {/* Month Dropdown */}
                <div className={`events-calendar-filters__options ${!openSections.month ? 'events-calendar-filters__options--accordion' : ''} d-lg-block`}>
                    <select
                        className="events-calendar-filters__select"
                        value={selectedMonth}
                        onChange={(e) => handleMonthChange(e.target.value)}
                    >
                        {months.map((month, index) => (
                            <option key={index} value={month.value}>
                                {month.label}
                            </option>
                        ))}
                    </select>
                </div>
            </div>
            
            {/* Topics Section */}
            <div className="events-calendar-filters__section" data-open={openSections.topics}>
                {/* Mobile Accordion Header */}
                <button 
                    className="events-calendar-filters__accordion-header d-lg-none"
                    onClick={() => toggleSection('topics')}
                    aria-expanded={openSections.topics}
                >
                    <h4 className="events-calendar-filters__section-title">{t('filter_topics_section')}</h4>
                    <i className={`fas fa-chevron-${openSections.topics ? 'up' : 'down'} events-calendar-filters__chevron`}></i>
                </button>
                
                {/* Desktop Title */}
                <h4 className="events-calendar-filters__section-title d-none d-lg-block">{t('filter_topics_section')}</h4>
                
                {/* Topics Options (multi-select checkboxes like EventFilters) */}
                <div className={`events-calendar-filters__options ${!openSections.topics ? 'events-calendar-filters__options--accordion' : ''} d-lg-block`}>
                    {topics.map((topic) => (
                        <label key={topic.id} className="events-calendar-filters__checkbox-label">
                            <input
                                type="checkbox"
                                className="events-calendar-filters__checkbox"
                                checked={selectedTopics.includes(topic.name)}
                                onChange={() => handleTopicChange(topic.name)}
                            />
                            <span className="events-calendar-filters__checkbox-text">{topic.label}</span>
                        </label>
                    ))}
                </div>
            </div>
            
            {/* Sub-Topics Section */}
            <div className="events-calendar-filters__section" data-open={openSections.subtopics}>
                {/* Mobile Accordion Header */}
                <button 
                    className="events-calendar-filters__accordion-header d-lg-none"
                    onClick={() => toggleSection('subtopics')}
                    aria-expanded={openSections.subtopics}
                >
                    <h4 className="events-calendar-filters__section-title">{t('filter_subtopics_section')}</h4>
                    <i className={`fas fa-chevron-${openSections.subtopics ? 'up' : 'down'} events-calendar-filters__chevron`}></i>
                </button>
                
                {/* Desktop Title */}
                <h4 className="events-calendar-filters__section-title d-none d-lg-block">{t('filter_subtopics_section')}</h4>
                
                {/* Subtopic Options */}
                <div className={`events-calendar-filters__options ${!openSections.subtopics ? 'events-calendar-filters__options--accordion' : ''} d-lg-block`}>
                    {/* Show loading state only when loading AND a specific topic is selected */}
                    {loadingSubtopics && selectedTopics && selectedTopics.length > 0 && !selectedTopics.includes('All') && (
                        <p className="events-calendar-filters__hint">Loading subtopics...</p>
                    )}
                    {/* Show "please select topic" message when "All" is selected */}
                    {selectedTopics && selectedTopics.includes('All') && (
                        <p className="events-calendar-filters__hint">Please select a topic to see available subtopics</p>
                    )}
                    {/* Show "no subtopics" message only when specific topic(s) selected but no subtopics found */}
                    {!loadingSubtopics && selectedTopics && selectedTopics.length > 0 && !selectedTopics.includes('All') && availableSubtopics.length === 0 && (
                        <p className="events-calendar-filters__hint">No subtopics available for selected topic</p>
                    )}
                    {/* Show subtopics only when not loading, specific topic(s) selected, and subtopics are available */}
                    {!loadingSubtopics && selectedTopics && selectedTopics.length > 0 && !selectedTopics.includes('All') && subTopics.length > 0 && (
                        <div className="events-calendar-filters__subtopics-list">
                            {subTopics.map((subTopic) => (
                                <label key={subTopic.id} className="events-calendar-filters__checkbox-label">
                                    <input
                                        type="checkbox"
                                        className="events-calendar-filters__checkbox"
                                        checked={selectedSubTopics.includes(subTopic.name)}
                                        onChange={() => handleSubTopicChange(subTopic.name)}
                                    />
                                    <span className="events-calendar-filters__checkbox-text">{subTopic.label}</span>
                                </label>
                            ))}
                        </div>
                    )}
                </div>
            </div>
            
            {/* Location Section */}
            <div className="events-calendar-filters__section" data-open={openSections.location}>
                {/* Mobile Accordion Header */}
                <button 
                    className="events-calendar-filters__accordion-header d-lg-none"
                    onClick={() => toggleSection('location')}
                    aria-expanded={openSections.location}
                >
                    <h4 className="events-calendar-filters__section-title">{t('filter_location_section')}</h4>
                    <i className={`fas fa-chevron-${openSections.location ? 'up' : 'down'} events-calendar-filters__chevron`}></i>
                </button>
                
                {/* Desktop Title */}
                <h4 className="events-calendar-filters__section-title d-none d-lg-block">{t('filter_location_section')}</h4>
                
                {/* Location Dropdown */}
                <div className={`events-calendar-filters__options ${!openSections.location ? 'events-calendar-filters__options--accordion' : ''} d-lg-block`}>
                    <select
                        className="events-calendar-filters__select"
                        value={selectedLocation}
                        onChange={(e) => handleLocationChange(e.target.value)}
                    >
                        {locations.map((location) => (
                            <option key={location.id} value={location.value}>
                                {location.label}
                            </option>
                        ))}
                    </select>
                </div>
            </div>
        </div>
    );
};

EventsCalendarFilters.propTypes = {
    onFilterChange: PropTypes.func.isRequired,
    initialFilters: PropTypes.object,
    filterOptions: PropTypes.object,
    viewMode: PropTypes.string
};

export default EventsCalendarFilters;

